import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel, TableSortLabel
} from '@mui/material';
import { Reply as ReplyIcon } from '@mui/icons-material';
import api from '../../utils/axios';
import { toast } from 'react-toastify';
import { getComparator, stableSort } from '../../utils/sortUtils';

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sorting State
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [status, setStatus] = useState('');
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/superadmin/tickets');
      if (res.data.success) {
        setTickets(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleReplyClick = (ticket) => {
    setSelectedTicket(ticket);
    setStatus(ticket.status);
    setReplyMessage('');
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = { status };
      if (replyMessage) payload.replyMessage = replyMessage;
      
      const res = await api.put(`/superadmin/tickets/${selectedTicket._id}`, payload);
      if (res.data.success) {
        toast.success('Ticket updated');
        fetchTickets();
        setOpenDialog(false);
      }
    } catch (err) {
      toast.error('Failed to update ticket');
    }
  };

  const getStatusColor = (s) => {
    switch(s) {
      case 'Open': return 'error';
      case 'In Progress': return 'warning';
      case 'Resolved': return 'success';
      default: return 'default';
    }
  };

  const sortedTickets = stableSort(tickets, getComparator(order, orderBy));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Support Tickets</Typography>
      
      <Card sx={{ mt: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'subject'}
                    direction={orderBy === 'subject' ? order : 'asc'}
                    onClick={() => handleRequestSort('subject')}
                  >
                    Subject
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'user.firstName'}
                    direction={orderBy === 'user.firstName' ? order : 'asc'}
                    onClick={() => handleRequestSort('user.firstName')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'church.name'}
                    direction={orderBy === 'church.name' ? order : 'asc'}
                    onClick={() => handleRequestSort('church.name')}
                  >
                    Church
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdAt')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTickets.map((ticket) => (
                <TableRow key={ticket._id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{ticket.subject}</TableCell>
                  <TableCell>
                    {ticket.user?.firstName} {ticket.user?.lastName}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {ticket.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{ticket.church?.name}</TableCell>
                  <TableCell>
                    <Chip label={ticket.status} color={getStatusColor(ticket.status)} size="small" />
                  </TableCell>
                  <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleReplyClick(ticket)} color="primary" title="Reply">
                      <ReplyIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {tickets.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} align="center">No tickets found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Reply Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ticket: {selectedTicket?.subject}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            <Box sx={{ p: 2, bgcolor: '#f4f6f8', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Original Message:</Typography>
              <Typography variant="body1">{selectedTicket?.message}</Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
              </Select>
            </FormControl>

            <TextField 
              fullWidth 
              label="Reply via Email (SendGrid)" 
              multiline 
              rows={4} 
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your response here... This will be emailed to the user."
            />
            
            {selectedTicket?.responses && selectedTicket.responses.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Previous Responses:</Typography>
                {selectedTicket.responses.map((resp, idx) => (
                  <Box key={idx} sx={{ p: 1.5, mb: 1, bgcolor: 'rgba(162, 89, 255, 0.1)', borderRadius: 2 }}>
                    <Typography variant="body2">{resp.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sent on {new Date(resp.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Save & Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
